import _ from "lodash";
import { getTimezoneOffset, format } from "date-fns-tz";

import {
  BYPASS_PRIVATE_DNS,
  BypassPrivateDns,
  isFailure,
  AttributesData,
  Frequency,
} from "@sportal/api";
import {
  LANGUAGE_COOKIE_KEY,
  DEFAULT_FREQUENCY,
  FALLBACK_LANGUAGE,
} from "./attributes.constants";

import { changeTranslation } from "../../i18n/translation.actions";
import { setCookie } from "../../common/utils/cookie";
import {
  Notificator,
  NotificationActionTypes,
} from "../../components/notification";
import { SBThunkAction } from "../redux.types";
import { getSubscriberId } from "../info/info.selectors";
import {
  AttributesActionTypes,
  CHANGE_LANGUAGE_FAILURE,
  CHANGE_LANGUAGE_SUCCESS,
  CHANGE_TIMEZONE_SUCCESS,
  CHANGE_TIMEZONE_FAILURE,
  LOAD_ATTRIBUTES,
  LOAD_ATTRIBUTES_FAILURE,
  LOAD_ATTRIBUTES_SUCCESS,
  CHANGE_TIME_FORMAT_SUCCESS,
  CHANGE_TIME_FORMAT_FAILURE,
  CHANGE_APR_SETTING_SUCCESS,
  CHANGE_APR_SETTING_FAILURE,
  UPDATE_EMAIL_ADDRESS_SUCCESS,
  UPDATE_EMAIL_ADDRESS_FAILURE,
  UPDATE_EMAIL_FREQUENCY_SUCCESS,
  UPDATE_EMAIL_FREQUENCY_FAILURE,
  LOAD_TIMEZONES_SUCCESS,
  LOAD_TIMEZONES_FAILURE,
  CHANGE_ATTRIBUTE,
  DISCARD_SCHEDULED_REPORTS_SETTINGS,
} from "./attributes.types";
import {
  getDefaultLanguage,
  getUseFallbackLanguage,
} from "../config.selectors";

export const loadAttributes =
  (): SBThunkAction<void, AttributesActionTypes> =>
  async (dispatch, getState, { api }) => {
    const state = getState();
    const subscriberId = getSubscriberId(state);

    dispatch(loadingAttributes());

    const result = await api.ssm.attributes.get(subscriberId);

    if (isFailure(result)) {
      dispatch(loadAttributesFailure(result.error));

      return Promise.reject(result.error);
    } else {
      const getLanguage = () => {
        const defaultConfigLanguage = getDefaultLanguage(state);
        const useFallbackLanguage = getUseFallbackLanguage(state);

        return (
          result.data.language ||
          defaultConfigLanguage ||
          (useFallbackLanguage ? FALLBACK_LANGUAGE : "")
        );
      };

      const attributes = {
        ...result.data,
        language: getLanguage(),
        frequency: result.data.frequency || DEFAULT_FREQUENCY,
        address: result.data.address || "",
      };

      setCookie(LANGUAGE_COOKIE_KEY, attributes.language);
      dispatch(loadAttributesSuccess(attributes));
    }
  };

export const getTimezones =
  () =>
  (dispatch, getState, { api }) => {
    const getOffset = (timezone) => getTimezoneOffset(timezone);

    const byOffsetComparator = (a, b) =>
      getOffset(a.value) - getOffset(b.value);

    const { accountSettings } = getState();

    if (!_.isEmpty(accountSettings.timezones)) {
      return;
    }

    return api.resources
      .getTimezones()
      .then((timezones) => {
        const now = Date.now();

        const tz = _.map(timezones, (timezone) => ({
          value: timezone,
          name: timezone, // will be translated in component
          offset: format(now, "xxx", {
            timeZone: timezone,
          }),
        })).sort(byOffsetComparator);

        dispatch(loadTimezonesSuccess(tz));
      })
      .catch((error) => {
        dispatch(loadTimezonesFailure(error));
        return Promise.reject();
      });
  };

// TODO: seems transformable to saveAttribute
//  Additional action on start, and resolve,
//  not sure that current behavior is right in case when we saved language but failed to load translations
//  it seems that we can break state so that then it will lead to error on load
export const changeLanguage =
  (
    language: string
  ): SBThunkAction<void, AttributesActionTypes | NotificationActionTypes> =>
  async (dispatch, getState, { api }) => {
    const subscriberId = getSubscriberId(getState());

    const failure = (error: Object & { code: number | string }) => {
      dispatch(Notificator.error("language_save_error"));
      dispatch(changeLanguageFailure(error));

      return Promise.reject(error);
    };

    try {
      const result = await api.ssm.attributes.updateLanguage(
        subscriberId,
        language
      );
      await dispatch(changeTranslation(language));

      if (isFailure(result)) {
        return failure(result.error);
      } else {
        setCookie(LANGUAGE_COOKIE_KEY, language);
        dispatch(changeLanguageSuccess(language));
      }
    } catch (e) {
      return failure(e);
    }
  };

export const changeTimezone =
  (
    timezone: string
  ): SBThunkAction<void, AttributesActionTypes | NotificationActionTypes> =>
  async (dispatch, getState, { api }) => {
    const subscriberId = getSubscriberId(getState());

    const result = await api.ssm.account.updateTimezone(subscriberId, timezone);

    if (isFailure(result)) {
      dispatch(Notificator.error("timezone_save_error"));
      dispatch(changeTimezoneFailure(result.error));

      return Promise.reject(result.error);
    } else {
      dispatch(changeTimezoneSuccess(result.data));
    }
  };

export const changeTimeFormat =
  (
    timeFormat24h: boolean
  ): SBThunkAction<void, AttributesActionTypes | NotificationActionTypes> =>
  async (dispatch, getState, { api }) => {
    const subscriberId = getSubscriberId(getState());

    const result = await api.ssm.attributes.updateTimeFormat(
      subscriberId,
      timeFormat24h
    );

    if (isFailure(result)) {
      dispatch(Notificator.error("timeformat_save_error"));
      dispatch(changeTimeFormatFailure(result.error));

      return Promise.reject(result.error);
    } else {
      dispatch(changeTimeFormatSuccess(result.data));
    }
  };

export const changeAPRSetting =
  (
    aprOn: boolean
  ): SBThunkAction<void, AttributesActionTypes | NotificationActionTypes> =>
  async (dispatch, getState, { api }) => {
    const subscriberId = getSubscriberId(getState());

    const result = await api.ssm.account.updateAPRSetting(subscriberId, aprOn);

    if (isFailure(result)) {
      dispatch(Notificator.error("apr_setting_save_error"));
      dispatch(changeAPRSettingFailure(result.error));

      return Promise.reject(result.error);
    } else {
      dispatch(
        changeAPRSettingSuccess(
          result.data[BYPASS_PRIVATE_DNS][BypassPrivateDns.ApplePrivateRelay]
        )
      );
    }
  };

export const saveScheduledReportsSettings = () => (dispatch, getState) => {
  const {
    accountSettings: {
      email,
      toSave: { email: newEmail },
    },
  } = getState();

  const shouldSaveEmail = newEmail.address !== email.address;
  const shouldSaveFrequency = newEmail.frequency !== email.frequency;

  return Promise.all(
    _.compact([
      shouldSaveEmail && dispatch(updateEmailAddress(newEmail.address)),
      shouldSaveFrequency && dispatch(updateEmailFrequency(newEmail.frequency)),
    ])
  );
};

const updateEmailAddress =
  (
    address: string
  ): SBThunkAction<void, AttributesActionTypes | NotificationActionTypes> =>
  async (dispatch, getState, { api }) => {
    const subscriberId = getSubscriberId(getState());

    const result = await api.ssm.attributes.updateEmailAddress(
      subscriberId,
      address
    );

    if (isFailure(result)) {
      dispatch(Notificator.error("email_save_error"));
      dispatch(updateEmailAddressFailure(result.error));

      return Promise.reject(result.error);
    } else {
      dispatch(updateEmailAddressSuccess(address));
    }
  };

const updateEmailFrequency =
  (
    frequency: Frequency
  ): SBThunkAction<void, AttributesActionTypes | NotificationActionTypes> =>
  async (dispatch, getState, { api }) => {
    const subscriberId = getSubscriberId(getState());

    const result = await api.ssm.attributes.updateEmailFrequency(
      subscriberId,
      frequency
    );

    if (isFailure(result)) {
      dispatch(Notificator.error("frequency_save_error"));
      dispatch(updateEmailFrequencyFailure(result.error));

      return Promise.reject(result.error);
    } else {
      dispatch(updateEmailFrequencySuccess(frequency));
    }
  };

export const discardScheduledReportsSettings = () => ({
  type: DISCARD_SCHEDULED_REPORTS_SETTINGS,
});

const loadingAttributes = (): AttributesActionTypes => ({
  type: LOAD_ATTRIBUTES,
});

const loadAttributesSuccess = (
  attributes: AttributesData
): AttributesActionTypes => ({
  type: LOAD_ATTRIBUTES_SUCCESS,
  payload: attributes,
});

const loadAttributesFailure = (
  error: object & { code: number | string }
): AttributesActionTypes => ({
  type: LOAD_ATTRIBUTES_FAILURE,
  payload: error,
});

const loadTimezonesSuccess = (timezones) => ({
  type: LOAD_TIMEZONES_SUCCESS,
  payload: timezones,
});

const loadTimezonesFailure = (error) => ({
  type: LOAD_TIMEZONES_FAILURE,
  payload: error,
});

const changeLanguageSuccess = (language: string): AttributesActionTypes => ({
  type: CHANGE_LANGUAGE_SUCCESS,
  payload: language,
});

const changeLanguageFailure = (
  error: object & { code: number | string }
): AttributesActionTypes => ({
  type: CHANGE_LANGUAGE_FAILURE,
  payload: error,
});

const changeTimezoneSuccess = (timezone: string): AttributesActionTypes => ({
  type: CHANGE_TIMEZONE_SUCCESS,
  payload: timezone,
});

const changeTimezoneFailure = (
  error: object & { code: number | string }
): AttributesActionTypes => ({
  type: CHANGE_TIMEZONE_FAILURE,
  payload: error,
});

const changeTimeFormatSuccess = (
  is24hourFormat: boolean
): AttributesActionTypes => ({
  type: CHANGE_TIME_FORMAT_SUCCESS,
  payload: is24hourFormat,
});

const changeTimeFormatFailure = (
  error: object & { code: number | string }
): AttributesActionTypes => ({
  type: CHANGE_TIME_FORMAT_FAILURE,
  payload: error,
});

export const changeAPRSettingSuccess = (
  aprOn: boolean
): AttributesActionTypes => ({
  type: CHANGE_APR_SETTING_SUCCESS,
  payload: aprOn,
});

const changeAPRSettingFailure = (
  error: object & { code: number | string }
): AttributesActionTypes => ({
  type: CHANGE_APR_SETTING_FAILURE,
  payload: error,
});

export const updateEmailAddressSuccess = (
  address: string
): AttributesActionTypes => ({
  type: UPDATE_EMAIL_ADDRESS_SUCCESS,
  payload: address,
});

const updateEmailAddressFailure = (
  error: object & { code: number | string }
): AttributesActionTypes => ({
  type: UPDATE_EMAIL_ADDRESS_FAILURE,
  payload: error,
});

export const updateEmailFrequencySuccess = (
  frequency: Frequency
): AttributesActionTypes => ({
  type: UPDATE_EMAIL_FREQUENCY_SUCCESS,
  payload: frequency,
});

const updateEmailFrequencyFailure = (
  error: object & { code: number | string }
): AttributesActionTypes => ({
  type: UPDATE_EMAIL_FREQUENCY_FAILURE,
  payload: error,
});

export const changeAddress = (address) => changeAttribute({ address });
export const changeFrequency = (frequency) => changeAttribute({ frequency });

export const changeAttribute = (attribute) => ({
  type: CHANGE_ATTRIBUTE,
  payload: attribute,
});
